import { useState, type ComponentProps, type FC } from 'react';
import translations from '../.vocab';
import {
  Box,
  IconChevron,
  IconSkills,
  Inline,
  Stack,
  Text,
  TooltipRenderer,
} from 'braid-design-system';
import { useTranslations } from '@vocab/react';
import type { JobApplicationCardProps } from '../JobApplicationCard';
import {
  trackMatchedQualitiesViewLess,
  trackMatchedQualitiesViewMore,
} from 'src/utils/events';
import * as styles from './MatchedQualities.css';

type Quality = JobApplicationCardProps['matchedQualities']['qualities'][number];
type MatchedQualitiesProps = {
  candidateFullname: string;
  qualities: Quality[];
} & Pick<JobApplicationCardProps, 'userId' | 'jobId' | 'seekAdvertiserId'>;

export const MatchedQualities: FC<MatchedQualitiesProps> = ({
  userId,
  jobId,
  candidateFullname,
  seekAdvertiserId,
  qualities,
}) => {
  const { t } = useTranslations(translations);
  const [expanded, setExpanded] = useState(false);

  const MAX_QUALITIES_TO_SHOW = 5;

  const { alwaysVisibleQualities, behindDisclosureQualities } =
    qualities.reduce<{
      alwaysVisibleQualities: Quality[];
      behindDisclosureQualities: Quality[];
    }>(
      (prev, quality, index) => {
        if (index + 1 <= MAX_QUALITIES_TO_SHOW) {
          prev.alwaysVisibleQualities.push(quality);
        } else {
          prev.behindDisclosureQualities.push(quality);
        }

        return prev;
      },
      {
        alwaysVisibleQualities: [],
        behindDisclosureQualities: [],
      },
    );

  const renderMatchedQualities = (qualityArray: Quality[]) =>
    qualityArray.map((quality) => (
      <MatchedQuality
        key={quality.id}
        skill={quality.displayLabel}
        isTopSkill={quality.isTop}
        candidateFullname={candidateFullname}
      />
    ));

  return (
    <Inline space={['xsmall', 'small']} alignY="center">
      {renderMatchedQualities(alwaysVisibleQualities)}

      {behindDisclosureQualities.length > 0 && (
        <Inline space={['xsmall', 'small']} alignY="center">
          {expanded ? (
            <>
              {renderMatchedQualities(behindDisclosureQualities)}
              <CustomDisclosure
                data={{ cy: 'hide-matched-qualities-button' }}
                isExpanded
                onClick={() => {
                  setExpanded(false);
                  if (!userId) {
                    return;
                  }
                  trackMatchedQualitiesViewLess({
                    seekAdvertiserId,
                    seekListingId: jobId,
                    userId,
                  });
                }}
              >
                {t('Hide')}
              </CustomDisclosure>
            </>
          ) : (
            <CustomDisclosure
              isExpanded={false}
              onClick={() => {
                setExpanded(true);
                if (!userId) {
                  return;
                }
                trackMatchedQualitiesViewMore({
                  seekAdvertiserId,
                  seekListingId: jobId,
                  userId,
                });
              }}
            >
              {t('matchQualityCount more', {
                matchQualityCount: behindDisclosureQualities.length,
              })}
            </CustomDisclosure>
          )}
        </Inline>
      )}
    </Inline>
  );
};

interface MatchedQualityProps {
  skill: string;
  isTopSkill: boolean;
  candidateFullname: string;
}

const MatchedQuality: FC<MatchedQualityProps> = ({
  candidateFullname,
  isTopSkill,
  skill,
}) => {
  const { t } = useTranslations(translations);
  return (
    <TooltipRenderer
      id="matched-qualities-tooltip"
      tooltip={
        isTopSkill ? (
          <Stack space="small">
            <Text>
              <IconSkills /> {t('Top skill')}
            </Text>
            <Text>
              {t(
                "We found this top skill in candidateFullname's application. Applications shortlisted for jobs similar to yours often have this top skill.",
                {
                  candidateFullname,
                },
              )}
            </Text>
          </Stack>
        ) : (
          <Text>
            {t(
              "We found this skill in candidateFullname's application that matches your job ad.",
              { candidateFullname },
            )}
          </Text>
        )
      }
    >
      {({ triggerProps }) => (
        <Box {...triggerProps}>
          <MatchedQualityTag isTopSkill={isTopSkill} skill={skill} />
        </Box>
      )}
    </TooltipRenderer>
  );
};

/**
 * Custom Tag for Matched quality based on https://github.com/seek-oss/braid-design-system/blob/master/packages/braid-design-system/src/lib/components/Tag/Tag.tsx
 * We need this because we need Text size to be `small` but there's no support for it at the time.
 */
const MatchedQualityTag: FC<{ isTopSkill: boolean; skill: string }> = ({
  isTopSkill,
  skill,
}) => (
  <Box
    display="flex"
    minWidth={0}
    alignItems="center"
    background="neutralSoft"
    paddingY="xxsmall"
    paddingLeft={isTopSkill ? 'xsmall' : 'small'}
    paddingRight="small"
    borderRadius="full"
  >
    <Box minWidth={0} title={skill}>
      <Text
        baseline={false}
        maxLines={1}
        icon={isTopSkill ? <IconSkills /> : undefined}
        size="small"
      >
        {skill}
      </Text>
    </Box>
  </Box>
);

const CustomDisclosure: FC<
  {
    isExpanded: boolean;
    onClick: () => void;
    children: string;
  } & Pick<ComponentProps<typeof Box>, 'data'>
> = ({ isExpanded, onClick, children, data }) => (
  <Box
    data={data}
    role="button"
    tabIndex={0}
    cursor="pointer"
    onClick={onClick}
    className={styles.underlineOnHover}
  >
    <Inline space="xxsmall" alignY="center">
      <Text size="small" tone="neutral">
        {children}
      </Text>
      <Text size="small">
        <IconChevron
          direction={isExpanded ? 'up' : 'down'}
          alignY="lowercase"
        />
      </Text>
    </Inline>
  </Box>
);
